import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { ApproveTransactionDialogContent } from '~/components/smart-accounts/ApproveTransactionDialogContent'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useHandleMerkleTransactionError } from '~/hooks/merkle-transactions/useHandleMerkleTransactionError'
import { useActiveOrgId } from '~/hooks/org'
import { useNavigation } from '~/hooks/useNavigation'
import {
  getSplitsErrorCodeString,
  useServerRequests,
} from '~/hooks/useRequests'
import { IAddress, OrgMerkleTransaction } from '~/types'

type UpdateSignersTransactionData = {
  smartAccountId: string
  smartAccountAddress: IAddress
  memo?: string
  newAuthenticatorIds: string[]
  newThreshold: number
  onSuccess?: (transaction: OrgMerkleTransaction) => void
  onConfirmation?: () => void
  onError?: () => void
  isRecoveryTx: boolean
}

export const useUpdateSmartAccountSigners = (): {
  createUpdateSignersTransaction: (
    txData: UpdateSignersTransactionData,
  ) => Promise<OrgMerkleTransaction | undefined>
  isProcessing: boolean
} => {
  const { isProcessing, serverPostRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const { setGlobalModalState } = useAuth()
  const activeOrgId = useActiveOrgId()
  const navigate = useNavigation()

  const handleMerkleTransactionError = useHandleMerkleTransactionError()

  const createUpdateSignersTransaction = useCallback(
    async (txData: UpdateSignersTransactionData) => {
      const transactionData = {
        smartAccountId: txData.smartAccountId,
        authenticatorIds: txData.newAuthenticatorIds,
        threshold: txData.newThreshold,
        memo: txData.memo,
        isRecoveryTx: txData.isRecoveryTx,
      }

      const result = await serverPostRequest<{
        merkleTransaction: OrgMerkleTransaction
      }>({
        path: '/api/smart_accounts/transactions/merkle/update_signers',
        params: {
          ...transactionData,
        },
        errorMessage: 'Failed to update signers',
        overrideErrorMessageOptions: handleMerkleTransactionError,
      })

      if (result) {
        const merkleTransaction = result.data.merkleTransaction
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.TRANSACTIONS],
          predicate: (query) => {
            return (
              Array.isArray(query.queryKey) &&
              query.queryKey[1]?.smartAccountId ===
                merkleTransaction.smartAccountId
            )
          },
        })
        queryClient.setQueryData(
          [QUERY_KEYS.TRANSACTION, activeOrgId, merkleTransaction.id],
          merkleTransaction,
        )

        toast.success('Transaction created', {
          id: merkleTransaction.id,
          description: (
            <TransactionTitle transaction={merkleTransaction} unstyled />
          ),
        })

        setGlobalModalState({
          isOpen: true,
          content: (
            <ApproveTransactionDialogContent
              transaction={merkleTransaction}
              globalModal
              onClose={() => {
                setGlobalModalState({ isOpen: false, content: null })
              }}
              onConfirmation={txData.onConfirmation}
              onSubmitError={(e) => {
                const isResyncTransactionCreated =
                  getSplitsErrorCodeString(e) ===
                  'MERKLE_TRANSACTION_SUBMISSION_FAILED'

                if (isResyncTransactionCreated) {
                  queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.TRANSACTIONS],
                  })
                  navigate(
                    `/accounts/${merkleTransaction.smartAccountAddress}/`,
                  )
                }
              }}
            />
          ),
        })

        txData.onSuccess?.(merkleTransaction)
        return merkleTransaction
      } else {
        txData.onError?.()
      }
    },
    [
      activeOrgId,
      queryClient,
      serverPostRequest,
      setGlobalModalState,
      handleMerkleTransactionError,
      navigate,
    ],
  )

  return {
    createUpdateSignersTransaction,
    isProcessing,
  }
}
